/* eslint-disable no-restricted-imports */
import React, {type ReactNode, useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import styles from './UIGameActionButton.module.scss'
import {UIGameFloatingText} from '../UIGameFloatingText/index.ts'
import {UIGameCount} from '../UIGameCount/UIGameCount.tsx'
import {type TranslationObject} from '@colonist/i18n'
import {Tooltip} from '@colonist/ui'
import {PlayerColor} from '@colonist/game-states'
import {type Placement} from '@floating-ui/dom'
import {audioManager, Sound} from '../../../../../code/client/shared/scripts/Sound.ts'
import BgButtonHighlight from '@static/images/bg_button_highlight.svg'
import BgButton from '@static/images/bg_button.svg'
import BgTradeButton from '@static/images/bg_button_trade.svg'
import BgTradeButtonHighlight from '@static/images/bg_button_trade_highlight.svg'
import {type ImageURL} from '@colonist/types'
import {BACKGROUND_TRADE_BUTTON_IMAGE, getCooldownOverlayClipPath} from './utils.ts'
import ExecuteButtonEmbargoOverlay from '@static/images/execute_button_embargo_overlay.svg'
import {AnimatedWaitIcon} from '../AnimatedWaitIcon/index.ts'

type ButtonType = 'normal' | 'trade'

export interface UIGameActionButtonProps {
    iconPath?: ImageURL
    disabled?: boolean | undefined
    shakingAnimation?: boolean
    helperText?: TranslationObject | undefined
    count?: ReactNode | undefined
    highlight?: boolean
    tooltipTitle?: ReactNode | string
    tooltipContent?: ReactNode | string
    tooltipCustomMenuComponent?: ReactNode
    tooltipPosition?: Placement
    tooltipMenuClassName?: string
    centerTextTooltip?: boolean
    flickerAnimation?: boolean
    scalePulseAnimation?: boolean
    onClick?: () => void | undefined
    onClickActionIfDisabled?: () => void | undefined
    soundOnClick?: Sound.SoundType | undefined
    className?: string | undefined
    countColor?: PlayerColor
    buttonType?: ButtonType
    backgroundColor?: PlayerColor | undefined
    isEmbargoed?: boolean | undefined
    isCreatingCounterOffer?: boolean | undefined
    showCooldownMask?: boolean | undefined
    cooldownTimeMs?: number | undefined
    tooltipDisabled?: boolean | undefined
    iconClassName?: string | undefined
    customIconComponent?: ReactNode
    id?: string | undefined
}

const getBackgroundImage = (highlight: boolean, buttonType: ButtonType, backgroundColor?: PlayerColor | undefined) => {
    if(buttonType === 'trade') {
        if(backgroundColor != null) {
            return BACKGROUND_TRADE_BUTTON_IMAGE[backgroundColor]
        }
        return highlight ? BgTradeButtonHighlight : BgTradeButton
    }
    return highlight ? BgButtonHighlight : BgButton
}

export const UIGameActionButton = ({
    iconPath,
    disabled = false,
    shakingAnimation,
    helperText,
    count,
    countColor = PlayerColor.Red,
    highlight = false,
    tooltipTitle,
    tooltipContent,
    centerTextTooltip = false,
    tooltipCustomMenuComponent,
    tooltipPosition = 'top',
    tooltipMenuClassName,
    flickerAnimation,
    scalePulseAnimation,
    onClick,
    onClickActionIfDisabled,
    soundOnClick = Sound.SoundType.Click,
    className,
    buttonType = 'normal',
    backgroundColor,
    isEmbargoed,
    isCreatingCounterOffer,
    showCooldownMask,
    cooldownTimeMs,
    tooltipDisabled,
    iconClassName,
    customIconComponent,
    id,
}: UIGameActionButtonProps) => {
    const [isShaking, setIsShaking] = useState(false)
    const [cooldownPercentage, setCooldownPercentage] = useState(0)
    const shakeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null)
    const cooldownStartTimeRef = useRef<number | null>(null)

    const clearCooldownInterval = () => {
        if(cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current)
            cooldownIntervalRef.current = null
        }
    }
    const handleClick = () => {
        audioManager.play(soundOnClick)
        if(shakingAnimation) {
            if(!isShaking) {
                setIsShaking(true)
            }
            if(shakeTimeoutRef.current) {
                clearTimeout(shakeTimeoutRef.current)
            }
            shakeTimeoutRef.current = setTimeout(() => {
                setIsShaking(false)
                shakeTimeoutRef.current = null
            }, 500)
        }
        if(disabled) {
            onClickActionIfDisabled?.()
            return
        }
        onClick?.()
        setCooldownPercentage(100)
        clearCooldownInterval()
    }

    useEffect(() => {
        if(shakeTimeoutRef.current) {
            clearTimeout(shakeTimeoutRef.current)
        }
    }, [])

    useEffect(() => {
        const hasCooldownTime = cooldownTimeMs != null && cooldownTimeMs > 0
        if(hasCooldownTime && showCooldownMask) {
            cooldownStartTimeRef.current = Date.now()
            setCooldownPercentage(0)

            const FRAME_RATE_30FPS_MS = 32
            cooldownIntervalRef.current = setInterval(() => {
                const currentTime = Date.now()
                const elapsed = currentTime - ((cooldownStartTimeRef.current ?? 0) || 0)
                const percentage = Math.min((elapsed / cooldownTimeMs) * 100, 100)

                setCooldownPercentage(percentage)

                if(percentage >= 100) {
                    clearCooldownInterval()
                    handleClick()
                }
            }, FRAME_RATE_30FPS_MS)
        }
        return () => {
            clearCooldownInterval()
        }
    }, [cooldownTimeMs, showCooldownMask])

    const hasCount = count != null
    const hasTooltip = tooltipContent != null && tooltipContent !== ''
        || tooltipTitle != null && tooltipTitle !== ''
        || tooltipCustomMenuComponent != null
    const hasHelperText = helperText != null
    const isTradeButton = buttonType === 'trade'
    const hasCustomIconComponent = customIconComponent != null && customIconComponent !== ''

    const button = (
        <div
            className={clsx(styles.root,
                className,
                {
                    [styles.shaking]: isShaking,
                    [styles.opacityPulse]: flickerAnimation,
                    [styles.scalePulse]: scalePulseAnimation,
                })}
            onClick={handleClick}
            id={id}
        >
            {showCooldownMask && (
                <img
                    className={styles.cooldownMask}
                    style={{
                        clipPath: getCooldownOverlayClipPath(cooldownPercentage),
                    }}
                    draggable={false}
                    src={getBackgroundImage(true, buttonType, backgroundColor)}
                />
            )}
            {isEmbargoed && (
                <img
                    className={styles.embargoOverlay}
                    draggable={false}
                    src={ExecuteButtonEmbargoOverlay}
                />
            )}
            {isCreatingCounterOffer && (
                <AnimatedWaitIcon className={styles.waitIcon} animationType="bounce" />
            )}
            <img
                className={clsx(styles.backgroundImage, {
                    [styles.backgroundDisabled]: isTradeButton && disabled,
                })}
                draggable={false}
                src={getBackgroundImage(highlight, buttonType, backgroundColor)} />
            <div className={clsx({
                [styles.foregroundDisabled]: disabled})}>
                {hasCustomIconComponent ?
                    <div className={clsx(styles.iconWrapper, iconClassName)}>
                        {customIconComponent}
                    </div>
                    : <img
                        className={clsx(styles.iconWrapper, iconClassName)}
                        draggable={false}
                        src={iconPath}
                    />}
                {hasCount && (
                    <UIGameCount className={clsx(styles.count)} color={countColor} count={count} />
                )}
            </div>
        </div>
    )

    if(hasTooltip && hasHelperText) {
        return (
            <Tooltip
                centerText={centerTextTooltip}
                content={tooltipContent}
                customMenuComponent={tooltipCustomMenuComponent}
                menuClassName={tooltipMenuClassName}
                position={tooltipPosition}
                disabled={tooltipDisabled}
                title={tooltipTitle}>
                <UIGameFloatingText direction="up" text={helperText}>
                    {button}
                </UIGameFloatingText>
            </Tooltip>
        )
    }

    if(hasHelperText) {
        return (
            <UIGameFloatingText direction="up" text={helperText}>
                {button}
            </UIGameFloatingText>
        )
    }

    if(hasTooltip) {
        return <Tooltip
            centerText={centerTextTooltip}
            content={tooltipContent}
            customMenuComponent={tooltipCustomMenuComponent}
            menuClassName={tooltipMenuClassName}
            position={tooltipPosition}
            disabled={tooltipDisabled}
            title={tooltipTitle}>
            {button}
        </Tooltip>
    }

    return button
}
